import React, { useEffect, useState } from 'react';
import './ShipmentDetails.css';
import ApiService, { initialAuthState } from '../../../services/ApiService';
import { useParams, useNavigate } from 'react-router';
import { ImCross } from "react-icons/im";
import { IoMdArrowRoundBack } from "react-icons/io";

const TimelineStep = ({ label, active }) => (
    <div className={`shipment-step ${active ? 'active' : ''}`}>
        <div className="dot"></div>
        <p>{label}</p>
    </div>
);

const ShipmentDetails = () => {
    const { shipmentId } = useParams();
    const navigate = useNavigate();

    const [shipment, setShipment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState();

    const staffDbId = localStorage.getItem('id')

    const fetchShipment = async () => {
        try {
            const res = await ApiService.get(`/shipment/get/${shipmentId}`);
            if (res.status) setShipment(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchShipment();
    }, []);

    const updateTracking = async () => {
        const trackingId = prompt('Enter Tracking ID');
        if (!trackingId) return;

        await ApiService.post('/shipment/updateTracking', {
            id: shipment.id,
            trackingId,
            companyCode: initialAuthState.companyCode,
            unitCode: initialAuthState.unitCode,
        });

        fetchShipment();
    };

    const updateStatus = async (status) => {
        try {
            const response = await ApiService.patch(`/shipment/update/${shipmentId}`, {
                id: shipment.id,
                status,
                companyCode: initialAuthState.companyCode,
                unitCode: initialAuthState.unitCode,
            });
            console.log(response, "res")
            setErrorMessage("");
            fetchShipment();

        } catch (err) {
            const errMessage = err?.data?.message || "Something went wrong";
            const parentOrderItemId = err?.data?.parentOrderItemId
            setErrorMessage({ errMessage, parentOrderItemId });
        }
    };

    const markDelivered = async () => {
        const confirmDelivery = window.confirm(
            'This will mark item has been delivered. Continue?'
        );

        if (!confirmDelivery) return;

        try {
            await updateStatus('delivered');
            alert('Item marked as Delivered');
        } catch (err) {
            alert('Failed to mark delivered');
        }
    };

    const markShipped = async () => {
        const confirmShip = window.confirm(
            'This will generate invoice and mark shipment as shipped. Continue?'
        );

        if (!confirmShip) return;

        try {
            await updateStatus('shipped');
            alert('Shipment marked as shipped & invoice generated');
        } catch (err) {
            alert('Failed to mark shipped');
        }
    };

    const markPacked = async () => {
        const confirmShip = window.confirm(
            'Are you sure you want to pack this shipment?'
        );

        if (!confirmShip) return;

        try {
            await updateStatus('packed');
            alert('Shipment packed');
        } catch (err) {
            alert('Failed to mark packed');
        }
    };

    const cancelShipment = async () => {
        const confirmCancel = window.confirm(
            'Are you sure you want to cancel this shipment?'
        );

        if (!confirmCancel) return;

        try {
            await ApiService.patch(`/shipment/cancel/${shipmentId}`, {
                id: shipment.id,
                companyCode: initialAuthState.companyCode,
                unitCode: initialAuthState.unitCode,
            });

            alert('Shipment cancelled successfully');
            fetchShipment();
        } catch (err) {
            alert('Failed to cancel shipment');
        }
    };

    const cancelShipmentItem = async (shipmentItemId) => {
        const confirmCancel = window.confirm(
            'Are you sure you want to remove this item from shipment?'
        );

        if (!confirmCancel) return;

        try {
            await ApiService.patch(`/shipment/item/cancel/${shipmentItemId}`, {
                shipmentId: shipment.id,
                companyCode: initialAuthState.companyCode,
                unitCode: initialAuthState.unitCode,
            });

            alert('Item removed from shipment');
            fetchShipment();
        } catch (err) {
            alert('Failed to cancel item');
        }
    };

    if (loading) return <div className="shipment-loader">Loading...</div>;
    if (!shipment) return <div className="shipment-error">Shipment not found</div>;

    const {
        shipmentNumber,
        status,
        branch,
        items,
        deliveryAddress,
        billingAddress,
        invoice,
        shippedAt,
        deliveredAt,
        expectedDeliveryDate,
        createdAt
    } = shipment;

    const StatusBadge = ({ status }) => (
        <span className={`admin-order-details-status-badge ${status?.toLowerCase()}`}>
            {status?.replaceAll('_', ' ')}
        </span>
    );

    const FLOW = ['created', 'packed', 'shipped', 'delivered'];
    const currentIndex = FLOW.indexOf(status);

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/all-orders');
        }
    };

    const formatDate = (date) =>
        date
            ? new Date(date).toLocaleDateString('en-IN')
            : '-';

    return (
        <div className="shipment-page">

            <div className="replace-request-button-container">
                <button onClick={handleBack} className="website-back-button">
                    <IoMdArrowRoundBack /> Back
                </button>
            </div>

            {/* 🔷 HEADER */}
            <div className="shipment-card header enhanced">
                <div>
                    <h2>{shipmentNumber}</h2>
                    <p>Shipment Details</p>
                    <p><strong>Shipment Created:</strong> {formatDate(createdAt) || '-'}</p>
                    <p><strong>Shipped:</strong> {shippedAt ? formatDate(shippedAt) : 'Not yet Shipped'}</p>
                    <p><strong>Delivered:</strong> {deliveredAt ? formatDate(deliveredAt) : 'Not delivered yet'}</p>
                    {deliveredAt === null && (
                        <p>
                            <strong>Expected Delivery:</strong> {formatDate(expectedDeliveryDate) || 'Not available'}
                        </p>
                    )}
                </div>

                <span className={`shipment-status ${status}`}>
                    {status}
                </span>
            </div>

            {/* 🔷 TIMELINE */}
            <div className="shipment-card">
                <h3>Shipment Timeline</h3>

                <div className="shipment-timeline">
                    <TimelineStep label="Created" active={currentIndex >= 0} />
                    <TimelineStep label="Packed" active={currentIndex >= 1} />
                    <TimelineStep label="Shipped" active={currentIndex >= 2} />
                    <TimelineStep label="Delivered" active={status === 'delivered'} />
                </div>
            </div>

            {/* 🔷 ADDRESS + BRANCH */}
            <div className="shipment-card grid-3">
                {/* DELIVERY */}
                <div>
                    <h3>Delivery Address</h3>
                    <p>{deliveryAddress?.name}</p>
                    <p>{deliveryAddress?.mobile}</p>
                    <p>{deliveryAddress?.addressLine}</p>
                    <p>{deliveryAddress?.city}, {deliveryAddress?.state}</p>
                    <p>{deliveryAddress?.pincode}</p>
                </div>

                {/* BILLING */}
                <div>
                    <h3>Billing Address</h3>
                    {billingAddress ? (
                        <>
                            <p>{billingAddress?.name}</p>
                            <p>{billingAddress?.mobile}</p>
                            <p>{billingAddress?.addressLine}</p>
                            <p>{billingAddress?.city}, {billingAddress?.state}</p>
                            <p>{billingAddress?.pincode}</p>
                        </>
                    ) : <p>Same as delivery</p>}
                </div>

                {/* BRANCH */}
                <div>
                    <h3>Shipping From</h3>
                    <p><strong>{branch?.branchName}</strong></p>
                    <p>{branch?.city}, {branch?.state}</p>
                </div>
            </div>

            {/* 🔥 ITEMS */}
            <div className="shipment-card">
                <h3>Items in Shipment</h3>

                {items?.length ? (
                    <table className="shipment-items-table enhanced">
                        <thead>
                            <tr>
                                <th>Device</th>
                                <th>Qty</th>
                                <th>Status</th>
                                <th>Base Price</th>
                                <th>Tax</th>
                                <th>Total</th>
                                <th>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {items.map(item => {
                                const oi = item.orderItem;
                                const price = Number(oi?.price || 0);
                                const tax = Number(oi?.totalTaxAmount || 0);
                                const total = Number(oi?.totalPrice || (price + tax) * item.quantity);

                                return (
                                    <tr key={item.id}>
                                        <td>
                                            <strong>{oi?.deviceName}</strong>
                                            <p className="sub-text">Item: {oi?.orderItemNumber}</p>
                                        </td>

                                        <td>{item.quantity}</td>

                                        <td>
                                            <StatusBadge status={oi?.itemStatus} />
                                        </td>

                                        <td>₹{price}</td>
                                        <td>₹{tax}</td>
                                        <td><strong>₹{total}</strong></td>

                                        <td>
                                            <button
                                                className="secondary-btn"
                                                onClick={() =>
                                                    navigate(`/order-item/${oi?.id}`)
                                                }
                                            >
                                                View
                                            </button>

                                            {status === 'created' && (
                                                <button
                                                    className="danger-btn"
                                                    onClick={() => cancelShipmentItem(item.id)}
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <p>No items found</p>
                )}
            </div>

            {/* 🔷 INVOICE */}
            {/* 🔥 ENHANCED: INVOICE DETAILS (AMAZON LEVEL) */}
            {invoice && (
                <div className="shipment-card invoice-card">

                    <div className="invoice-header">
                        <div>
                            <h3>Invoice Details</h3>
                            <p className="invoice-number">#{invoice.invoiceNumber}</p>
                        </div>

                        <span className="invoice-status success">
                            Generated
                        </span>
                    </div>

                    <div className="invoice-grid">

                        <div className="invoice-block">
                            <p><strong>Invoice ID:</strong> {invoice.id}</p>
                            <p><strong>Shipment:</strong> {shipmentNumber}</p>
                            <p><strong>Order ID:</strong> {shipment?.orderId}</p>
                        </div>

                        <div className="invoice-block">
                            <p><strong>Generated On:</strong> {new Date().toLocaleString()}</p>
                            <p><strong>Status:</strong> Completed</p>
                            <p><strong>Currency:</strong> INR</p>
                        </div>

                    </div>

                    {/* 🔥 PRICE SUMMARY FROM ITEMS */}
                    {/* <div className="invoice-price-summary">
                        <h4>Price Summary</h4>

                        {items?.map((item, i) => {
                            const price = Number(item.orderItem?.price || 0);
                            const qty = Number(item.quantity || 0);
                            const tax = Number(item.orderItem?.totalTaxAmount || 0);

                            return (
                                <div key={i} className="invoice-row">
                                    <span>{item.deviceName} × {qty}</span>
                                    <span>₹{(price * qty).toFixed(2)}</span>
                                </div>
                            );
                        })}

                        <div className="invoice-divider" />

                        <div className="invoice-row">
                            <span>Total Tax</span>
                            <span>
                                ₹{items?.reduce((acc, item) =>
                                    acc + Number(item.orderItem?.totalTaxAmount || 0), 0
                                ).toFixed(2)}
                            </span>
                        </div>

                        <div className="invoice-row total">
                            <span>Grand Total</span>
                            <span>
                                ₹{items?.reduce((acc, item) =>
                                    acc + Number(item.orderItem?.totalPrice || 0), 0
                                ).toFixed(2)}
                            </span>
                        </div>
                    </div> */}

                    {/* 🔥 ACTIONS */}
                    <div className="invoice-actions">

                        <button
                            className="primary-btn"
                            onClick={() => window.open(invoice.invoicePdfUrl, '_blank')}
                        >
                            View Invoice
                        </button>

                        {/* <button
                            className="secondary-btn"
                            onClick={() => window.open(invoice.invoicePdfUrl, '_blank')}
                        >
                            Download PDF
                        </button> */}

                        <button
                            className="warning-btn"
                            disabled
                        // onClick={async () => {
                        //     const confirmRegen = window.confirm(
                        //         "Regenerate invoice? This will overwrite existing invoice."
                        //     );

                        //     if (!confirmRegen) return;

                        //     try {
                        //         await ApiService.post('/invoice/regenerate', {
                        //             shipmentId: shipment.id,
                        //             companyCode: initialAuthState.companyCode,
                        //             unitCode: initialAuthState.unitCode,
                        //         });

                        //         alert('Invoice regenerated successfully');
                        //         fetchShipment();
                        //     } catch (err) {
                        //         console.error(err);
                        //         alert('Failed to regenerate invoice');
                        //     }
                        // }}
                        >
                            Regenerate Invoice
                        </button>

                    </div>
                </div>
            )}

            {/* 🔷 ERROR */}
            {errorMessage && (
                <div className="error-banner">
                    <div className="error-content">
                        <ImCross className="error-icon" />

                        <div className="error-text">
                            <p className="error-title">Action Required</p>
                            <p className="error-message">{errorMessage.errMessage}</p>
                        </div>
                    </div>

                    {errorMessage.parentOrderItemId && (
                        <button
                            className="error-action-btn"
                            onClick={() =>
                                navigate(`/order-item/${errorMessage.parentOrderItemId}`)
                            }
                        >
                            Fix Issue →
                        </button>
                    )}
                </div>
            )}

            {/* 🔥 ACTIONS */}
            {status !== 'delivered' && (
                <div className="shipment-card">
                    <h3>Actions</h3>

                    {status !== 'cancelled' && (
                        <>
                            {status === 'created' && (
                                <>
                                    <button onClick={markPacked}>
                                        Pack Shipment
                                    </button>

                                    <button
                                        className="danger-btn"
                                        onClick={cancelShipment}
                                    >
                                        Cancel Shipment
                                    </button>
                                </>
                            )}

                            {status === 'packed' && (
                                <>
                                    <button onClick={markShipped}>
                                        Mark Shipped & Generate Invoice
                                    </button>

                                    <button
                                        className="danger-btn"
                                        onClick={cancelShipment}
                                    >
                                        Cancel Shipment
                                    </button>
                                </>
                            )}

                            {status === 'shipped' && (
                                <>
                                    {/* <button onClick={() => updateStatus('out_for_delivery')}>
                                        Out for Delivery
                                    </button> */}

                                    <button onClick={() => markDelivered('delivered')}>
                                        Mark Delivered
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}

        </div>
    );
};

export default ShipmentDetails;




















// import React, { useEffect, useState } from 'react';
// import './ShipmentDetails.css';
// import ApiService, { initialAuthState } from '../../../services/ApiService';
// import { useParams, useNavigate } from 'react-router';
// import { ImCross } from "react-icons/im";

// const TimelineStep = ({ label, active }) => (
//     <div className={`shipment-step ${active ? 'active' : ''}`}>
//         <div className="dot"></div>
//         <p>{label}</p>
//     </div>
// );

// const ShipmentDetails = () => {
//     const { shipmentId } = useParams();
//     const navigate = useNavigate();

//     const [shipment, setShipment] = useState(null);
//     const [loading, setLoading] = useState(true);
//     const [errorMessage, setErrorMessage] = useState();

//     const staffDbId = localStorage.getItem('id')

//     const fetchShipment = async () => {
//         try {
//             const res = await ApiService.get(`/shipment/get/${shipmentId}`);
//             if (res.status) setShipment(res.data);
//         } catch (err) {
//             console.error(err);
//         } finally {
//             setLoading(false);
//         }
//     };

//     useEffect(() => {
//         fetchShipment();
//     }, []);

//     const updateTracking = async () => {
//         const trackingId = prompt('Enter Tracking ID');
//         if (!trackingId) return;

//         await ApiService.post('/shipment/updateTracking', {
//             id: shipment.id,
//             trackingId,
//             companyCode: initialAuthState.companyCode,
//             unitCode: initialAuthState.unitCode,
//         });

//         fetchShipment();
//     };



//     const updateStatus = async (status) => {
//         try {
//             const response = await ApiService.patch(`/shipment/update/${shipmentId}`, {
//                 id: shipment.id,
//                 status,
//                 staffId: staffDbId,
//                 companyCode: initialAuthState.companyCode,
//                 unitCode: initialAuthState.unitCode,
//             });

//             console.log(response, "res")

//             setErrorMessage(""); // clear old errors
//             fetchShipment();

//         } catch (err) {
//             console.error("Update failed:", err);
//             console.log("FULL ERROR:", err);
//             const errMessage =
//                 err?.data?.message || "Something went wrong";

//             const parentOrderItemId = err?.data?.parentOrderItemId

//             const message = { errMessage, parentOrderItemId }

//             setErrorMessage(message);
//         }
//     };

//     // ✅ NEW: Mark shipped with confirmation (invoice trigger)
//     const markShipped = async () => {
//         const confirmShip = window.confirm(
//             'This will generate invoice and mark shipment as shipped. Continue?'
//         );

//         if (!confirmShip) return;

//         try {
//             await updateStatus('shipped');
//             alert('Shipment marked as shipped & invoice generated');
//         } catch (err) {
//             console.error(err);
//             alert('Failed to mark shipped');
//         }
//     };

//     const markPacked = async () => {
//         const confirmShip = window.confirm(
//             'Are you sure you want to pack this shipment?'
//         );

//         if (!confirmShip) return;

//         try {
//             await updateStatus('packed');
//             alert('Shipment packed');
//         } catch (err) {
//             console.error(err);
//             alert('Failed to mark shipped');
//         }
//     };

//     // ✅ NEW: Cancel shipment
//     const cancelShipment = async () => {
//         const confirmCancel = window.confirm(
//             'Are you sure you want to cancel this shipment?'
//         );

//         if (!confirmCancel) return;

//         try {
//             await ApiService.patch(`/shipment/cancel/${shipmentId}`, {
//                 id: shipment.id,
//                 companyCode: initialAuthState.companyCode,
//                 unitCode: initialAuthState.unitCode,
//             });

//             alert('Shipment cancelled successfully');
//             fetchShipment();
//         } catch (err) {
//             console.error(err);
//             alert('Failed to cancel shipment');
//         }
//     };

//     const cancelShipmentItem = async (shipmentItemId) => {
//         const confirmCancel = window.confirm(
//             'Are you sure you want to remove this item from shipment?'
//         );

//         if (!confirmCancel) return;

//         try {
//             await ApiService.patch(`/shipment/item/cancel/${shipmentItemId}`, {
//                 shipmentId: shipment.id,
//                 companyCode: initialAuthState.companyCode,
//                 unitCode: initialAuthState.unitCode,
//             });

//             alert('Item removed from shipment');
//             fetchShipment();
//         } catch (err) {
//             console.error(err);
//             alert('Failed to cancel item from shipment');
//         }
//     };

//     if (loading) return <div className="shipment-loader">Loading...</div>;
//     if (!shipment) return <div className="shipment-error">Shipment not found</div>;

//     const {
//         shipmentNumber,
//         status,
//         branch,
//         items,
//         deliveryAddress,
//         invoice,
//     } = shipment;

//     const StatusBadge = ({ status }) => (
//         <span className={`admin-order-details-status-badge ${status?.toLowerCase()}`}>
//             {status}
//         </span>
//     );

//     const FLOW = ['created', 'packed', 'shipped', 'delivered'];

//     const currentIndex = FLOW.indexOf(status);

//     return (
//         <div className="shipment-page">

//             {/* 🔷 HEADER */}
//             <div className="shipment-card header">
//                 <div>
//                     <h2>{shipmentNumber}</h2>
//                     <p>Shipment Details</p>
//                 </div>

//                 <span className={`shipment-status ${status}`}>
//                     {status}
//                 </span>
//             </div>

//             {/* 🔷 TIMELINE */}
//             <div className="shipment-card">
//                 <h3>Shipment Timeline</h3>

//                 <div className="shipment-timeline">
//                     {/* <TimelineStep label="Created" active />
//                     <TimelineStep label="Packed" active={status === 'packed' || status !== 'created'} />
//                     <TimelineStep label="Shipped" active={(status === 'shipped' || status !== 'created')} /> */}
//                     {/* <TimelineStep
//                         label="Out for Delivery"
//                         active={status === 'out_for_delivery' || status === 'delivered'}
//                     /> */}
//                     <TimelineStep label="Created" active={currentIndex >= 0} />

//                     <TimelineStep
//                         label="Packed"
//                         active={currentIndex >= 1}
//                     />

//                     <TimelineStep
//                         label="Shipped"
//                         active={currentIndex >= 2}
//                     />
//                     <TimelineStep
//                         label="Delivered"
//                         active={status === 'delivered'}
//                     />
//                 </div>
//             </div>

//             {/* 🔷 TRACKING */}
//             {/* <div className="shipment-card">
//                 <h3>Tracking</h3>

//                 <p><strong>Tracking ID:</strong> {shipment.trackingId || 'Not Available'}</p>

//                 <button onClick={updateTracking}>
//                     Update Tracking
//                 </button>
//             </div> */}

//             {/* 🔥 UPDATED: ORDER ITEMS (INVOICE READY) */}
//             <div className="shipment-card">
//                 <h3>Items in Shipment</h3>

//                 {items?.length ? (
//                     <table className="shipment-items-table">
//                         <thead>
//                             <tr>
//                                 <th>Device</th>
//                                 <th>Qty</th>
//                                 <th>Branch</th>
//                                 <th>Status</th>
//                                 <th>Price</th>
//                                 <th>Tax</th>
//                                 <th>Total</th>
//                                 <th>Actions</th>
//                             </tr>
//                         </thead>

//                         <tbody>
//                             {items.map(item => {
//                                 const price = item.orderItem?.price || 0;
//                                 const tax = item.orderItem?.taxAmount || 0;
//                                 const total = (price + tax) * item.shipmentQuantity;

//                                 return (
//                                     <tr key={item.id}>
//                                         <td>{item.orderItem?.deviceName}</td>
//                                         <td>{item.quantity}</td>
//                                         <td>{branch?.branchName || 'Not Assigned'}</td>

//                                         <td>
//                                             <StatusBadge status={item.orderItem?.itemStatus || 'pending'} />
//                                         </td>

//                                         <td>₹{price}</td>
//                                         <td>₹{tax}</td>
//                                         <td>₹{total}</td>

//                                         {/* 🔥 NEW ACTIONS COLUMN */}
//                                         <td>

//                                             {/* 👁 VIEW */}
//                                             <button
//                                                 className="secondary-btn"
//                                                 onClick={() =>
//                                                     navigate(`/order-item/${item.orderItem?.id}`)
//                                                 }
//                                             >
//                                                 View
//                                             </button>

//                                             {/* ❌ CANCEL ITEM */}
//                                             {status === 'created' && (
//                                                 <button
//                                                     className="danger-btn"
//                                                     onClick={() => cancelShipmentItem(item.id)}
//                                                 >
//                                                     Cancel
//                                                 </button>
//                                             )}

//                                         </td>
//                                     </tr>
//                                 );
//                             })}
//                         </tbody>
//                     </table>
//                 ) : (
//                     <p>No items found</p>
//                 )}
//             </div>

//             {/* 🔷 DELIVERY ADDRESS */}
//             {deliveryAddress && (
//                 <div className="shipment-card">
//                     <h3>Delivery Address</h3>

//                     <p>{deliveryAddress.name}</p>
//                     <p>{deliveryAddress.mobile}</p>
//                     <p>{deliveryAddress.addressLine}</p>
//                     <p>{deliveryAddress.city}, {deliveryAddress.state}</p>
//                     <p>{deliveryAddress.pincode}</p>
//                 </div>
//             )}

//             {/* 🔥 UPDATED: INVOICE DETAILS */}
//             {invoice && (
//                 <div className="shipment-card">
//                     <h3>Invoice Generated - <strong>
//                         {invoice.invoiceNumber}</strong></h3>

//                     {/* <p><strong>Invoice No:</strong> {invoice.invoiceNumber}</p>
//                     <p><strong>Total:</strong> ₹{invoice.totalAmount}</p>
//                     <p><strong>Tax:</strong> ₹{invoice.taxAmount}</p> */}

//                     <button onClick={() => window.open(invoice.invoicePdfUrl, '_blank')}>
//                         View Invoice
//                     </button>
//                 </div>
//             )}

//             {errorMessage && (
//                 <div className="error-banner">
//                     <div className="error-content">
//                         <ImCross className="error-icon" />

//                         <div className="error-text">
//                             <p className="error-title">Action Required</p>
//                             <p className="error-message">{errorMessage.errMessage}</p>
//                         </div>
//                     </div>

//                     {errorMessage.parentOrderItemId && (
//                         <button
//                             className="error-action-btn"
//                             onClick={() =>
//                                 navigate(`/order-item/${errorMessage.parentOrderItemId}`)
//                             }
//                         >
//                             View Parent Item →
//                         </button>
//                     )}
//                 </div>
//             )}

//             {/* 🔥 ACTIONS */}
//             {status !== 'delivered'
//                 && <div className="shipment-card">
//                     <h3>Actions</h3>

//                     {status !== 'cancelled' && (
//                         <>
//                             {status === 'created' && (
//                                 <>
//                                     <button onClick={markPacked}>
//                                         Pack Shipment
//                                     </button>

//                                     <button
//                                         className="danger-btn"
//                                         onClick={cancelShipment}
//                                     >
//                                         Cancel Shipment
//                                     </button>
//                                 </>
//                             )}

//                             {status === 'packed' && (
//                                 <>
//                                     <button onClick={markShipped}>
//                                         Mark Shipped & Generate Invoice
//                                     </button>

//                                     <button
//                                         className="danger-btn"
//                                         onClick={cancelShipment}
//                                     >
//                                         Cancel Shipment
//                                     </button>
//                                 </>
//                             )}

//                             {status === 'shipped' && (
//                                 <button onClick={() => updateStatus('out_for_delivery')}>
//                                     Out for Delivery
//                                 </button>
//                             )}

//                             {status === 'shipped' && (
//                                 <button onClick={() => updateStatus('delivered')}>
//                                     Mark Delivered
//                                 </button>
//                             )}
//                         </>
//                     )}
//                 </div>
//             }


//         </div>
//     );
// };

// export default ShipmentDetails;



